/**
 * backgroundHandoff.js — hand a finished background query to the results page.
 *
 * "View" on a completed background query used to JSON the whole result array
 * into sessionStorage. SGP queries carry up to 500 items, each duplicated in
 * `raw`, which overflows the ~5MB quota. setItem threw, the catch swallowed it,
 * `sgp:fromBackground` was never written, and the results page re-ran the
 * search from 0 ("Searching for bets…" with Fixtures 0).
 *
 * The results already live in memory (BackgroundQueryContext.activeQueries), so
 * the handoff stays in memory too: View stashes it here, the results page takes
 * it by queryId. Nothing large touches sessionStorage.
 */

// Keys the old sessionStorage handoff wrote. Cleared on every handoff so a
// stale 'true' flag or a previous query's results can never leak into a new one.
export const LEGACY_HANDOFF_KEYS = [
  'sgp:backgroundResults',
  'sgp:fromBackground',
  'sgp:myBooksResults',
  'sgp:queryId',
  'sgp:preferredSportsbook',
  'sgp:lineMetadata',
  'sgp:queryClassification',
  'sgp:referenceTime',
  'sgp:relaxMinBooks',
];

let _pending = null;

/**
 * Build the handoff for a background query object. Returns null unless the
 * query has completed — a pending/failed query has nothing to show.
 */
export function buildBackgroundHandoff(query) {
  if (!query || query.status !== 'completed') return null;
  const results = Array.isArray(query.results) ? query.results : [];
  const myBooksResults = Array.isArray(query.myBooksResults) ? query.myBooksResults : [];
  // When every result is below the min-books threshold (raw > 0, all-books 0),
  // relax the filter so the page shows something instead of an empty list.
  // countBackgroundQuery (utils/resultCounts) stores the decision so the card's
  // counts and the page agree; older stored queries fall back to the raw rule.
  const relaxMinBooks = typeof query.relaxMinBooks === 'boolean'
    ? query.relaxMinBooks
    : (!query._preferredSportsbook
      && (query.allBooksCount ?? 0) === 0
      && (query.rawResultCount ?? 0) > 0);
  return {
    queryId: query.id,
    prompt: query.prompt || '',
    results,
    myBooksResults,
    // A book that priced none of these results must not become the filter (it hid everything).
    preferredSportsbook: query._bookUnavailable ? null : (query._preferredSportsbook || null),
    lineMetadata: query._lineMetadata || null,
    queryClassification: query._queryClassification || null,
    bookUnavailable: query._bookUnavailable || null,
    message: query._message || null,
    referenceTime: query.referenceTime || null,
    relaxMinBooks,
  };
}

export function stashBackgroundHandoff(handoff) {
  _pending = handoff || null;
}

/**
 * Take (and clear) the stashed handoff. With a queryId, only a matching
 * handoff is returned, so a late event for query A can't render query B.
 */
export function takeBackgroundHandoff(queryId) {
  const h = _pending;
  if (!h) return null;
  if (queryId && h.queryId !== queryId) return null;
  _pending = null;
  return h;
}

export function clearLegacyHandoffKeys(storage) {
  if (!storage) return;
  for (const key of LEGACY_HANDOFF_KEYS) {
    try { storage.removeItem(key); } catch { /* ignore */ }
  }
}

/**
 * Requested line from the handoff's line metadata, falling back to the query
 * text ("over 27.5", "+14"). Same rules the old sessionStorage path used.
 */
export function resolveRequestedLine(lineMetadata, prompt) {
  if (lineMetadata && lineMetadata.lineThreshold !== null && lineMetadata.lineThreshold !== undefined) {
    const line = Number(lineMetadata.lineThreshold);
    if (Number.isFinite(line)) return { line, direction: lineMetadata.lineDirection || null };
  }
  const qText = prompt || '';
  const lineMatch = qText.match(/(?:over|under|o|u)\s+(\d+\.?\d*)/i);
  if (lineMatch) {
    // Word match: "Thunder over 220.5" is an over, not an under.
    const isUnder = /\bunder\b/i.test(qText) || /\bu\s/i.test(qText);
    return { line: parseFloat(lineMatch[1]), direction: isUnder ? 'under' : 'over' };
  }
  const spreadMatch = qText.match(/([+-])\s*(\d+\.?\d*)/);
  if (spreadMatch) {
    return { line: parseFloat(spreadMatch[2]) * (spreadMatch[1] === '-' ? -1 : 1), direction: null };
  }
  return { line: null, direction: null };
}
